/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';
import {
    Box, Typography, Avatar, TextField,
    InputAdornment, CircularProgress, Autocomplete, alpha,
} from '@mui/material';
import Iconify from 'src/components/Iconify';
import { callApi } from 'src/utils/apiCall';
import { common } from 'src/theme/palette';

// Keep fields flat — nested braces like lead_contact{email,phone} break URL parsing on the search endpoint
const SEARCH_FIELDS = 'id,first_name,last_name,lead_contact';

const PatientSelector = ({ practiceId, selectedLead, onSelect }) => {
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [inputValue, setInputValue] = useState('');

    const doSearch = useCallback(
        debounce(async (query) => {
            if (!query || query.length < 2 || !practiceId) {
                setSearchResults([]);
                setSearchLoading(false);
                return;
            }
            setSearchLoading(true);
            try {
                const res = await callApi(
                    `/api/v1/practices/${practiceId}/leads/search/?query=${encodeURIComponent(query)}&limit=10&offset=0&ordering=-created_at&is_archived=false&fields={${SEARCH_FIELDS}}`
                );
                if ((res.ok || res.status === 200) && res.data?.results) {
                    setSearchResults(res.data.results);
                } else {
                    setSearchResults([]);
                }
            } catch {
                setSearchResults([]);
            }
            setSearchLoading(false);
        }, 300),
        [practiceId]
    );

    const getName = (lead) =>
        `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || 'Unnamed';

    const getInitials = (lead) =>
        `${(lead.first_name || '?')[0]}${(lead.last_name || '?')[0]}`.toUpperCase();

    return (
        <Box>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Search for a patient
            </Typography>

            <Autocomplete
                fullWidth
                filterOptions={(x) => x}
                options={searchResults}
                loading={searchLoading}
                value={selectedLead}
                inputValue={inputValue}
                onChange={(_, newVal) => onSelect(newVal)}
                onInputChange={(_, val, reason) => {
                    setInputValue(val);
                    if (reason === 'input') doSearch(val);
                }}
                getOptionLabel={(opt) => getName(opt)}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        size="small"
                        placeholder="Search by name, email or phone..."
                        InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Iconify icon="eva:search-fill" width={18} sx={{ color: 'text.disabled' }} />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <>
                                    {searchLoading && <CircularProgress size={14} />}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '10px',
                            },
                        }}
                    />
                )}
                renderOption={(props, lead) => (
                    <Box
                        component="li"
                        {...props}
                        key={lead.id}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            py: 1.25,
                            px: 2,
                            '&:hover': { bgcolor: alpha(common.mintyZest, 0.04) },
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            '&:last-of-type': { borderBottom: 'none' },
                        }}
                    >
                        <Avatar sx={{ width: 32, height: 32, fontSize: '12px', fontWeight: 700, bgcolor: alpha('#078DEE', 0.12), color: '#078DEE' }}>
                            {getInitials(lead)}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="subtitle2" noWrap sx={{ fontWeight: 600, fontSize: '13px' }}>
                                {getName(lead)}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                {lead.lead_contact?.email && (
                                    <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '11px' }}>
                                        {lead.lead_contact.email}
                                    </Typography>
                                )}
                                {lead.lead_contact?.phone && (
                                    <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '11px' }}>
                                        {lead.lead_contact.phone}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    </Box>
                )}
                noOptionsText={inputValue.length < 2 ? 'Type at least 2 characters to search' : 'No patients found'}
                PaperComponent={({ children, ...p }) => (
                    <Box
                        {...p}
                        sx={{
                            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                            borderRadius: '12px',
                            border: '1px solid',
                            borderColor: 'divider',
                            mt: 0.5,
                            overflow: 'hidden',
                        }}
                    >
                        {children}
                    </Box>
                )}
            />

            {selectedLead && (
                <Box
                    sx={{
                        mt: 2,
                        p: 2,
                        borderRadius: '12px',
                        bgcolor: alpha(common.mintyZest, 0.06),
                        border: '1px solid',
                        borderColor: alpha(common.mintyZest, 0.2),
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                    }}
                >
                    <Avatar sx={{ width: 40, height: 40, fontSize: '14px', fontWeight: 700, bgcolor: alpha(common.mintyZest, 0.15), color: common.mintyZest }}>
                        {getInitials(selectedLead)}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '14px' }}>
                            {getName(selectedLead)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {selectedLead.lead_contact?.email || selectedLead.lead_contact?.phone || 'No contact info'}
                        </Typography>
                    </Box>
                    <Iconify icon="eva:checkmark-circle-2-fill" sx={{ color: common.mintyZest, width: 22 }} />
                </Box>
            )}
        </Box>
    );
};

PatientSelector.propTypes = {
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    selectedLead: PropTypes.object,
    onSelect: PropTypes.func.isRequired,
};

PatientSelector.defaultProps = {
    selectedLead: null,
};

export default PatientSelector;
